import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, X, Sparkles, Calendar, IndianRupee, Zap } from "lucide-react";

const formatINR = (n) =>
  `₹${new Intl.NumberFormat("en-IN").format(Math.max(0, Math.round(n || 0)))}`;

const PLAN_THEME = {
  starter: { color: "#378ADD", label: "Starter" },
  pro: { color: "#FF6B00", label: "Pro" },
  family: { color: "#1D9E75", label: "Family" },
};

// "What you'll unlock today" — headline value-adds keyed by transition.
// Keep each list to 4 bullets max so the modal stays scannable.
const UNLOCKS = {
  // To Starter
  "free>starter": [
    "Unlimited invoice uploads — no more 5-file ceiling",
    "Saturday 9 AM smart grocery list on WhatsApp",
    "Live budget alerts at 60 / 80 / 100% of your monthly cap",
    "Restock predictions so you never run out of essentials",
  ],
  // To Pro
  "free>pro": [
    "Everything in Starter + all food-delivery apps (Zomato, Swiggy, Uber Eats)",
    "Cook vs Order calculator — exact rupees you'd save by skipping orders",
    "FMCG price comparison across Blinkit, Zepto, BigBasket",
    "Monthly downloadable PDF intelligence report",
  ],
  "starter>pro": [
    "Zomato, Swiggy Food, Uber Eats, BigBasket — added to your dashboard",
    "Unified 360 food view (grocery + restaurant + dining out)",
    "Cook vs Order calculator + FMCG cross-app price compare",
    "Emotional spending alerts (late nights, weekend splurges)",
  ],
  // To Family
  "free>family": [
    "Every Pro feature, scaled across up to 4 family members",
    "Shared household budget + per-member spending breakdown",
    "Individual WhatsApp connections for each member",
    "Combined family savings report, delivered weekly",
  ],
  "starter>family": [
    "All Pro features + 4-member household coverage",
    "Shared family budget dashboard with live contributions",
    "Per-member insights without juggling 4 separate logins",
    "Dedicated account manager + early access to new features",
  ],
  "pro>family": [
    "Add up to 4 family members under one household",
    "Shared family budget + per-member spending breakdown",
    "Individual WhatsApp connections for every member",
    "Family savings report — combined household intelligence",
  ],
};

const getUnlocks = (fromPlan, toPlan) => {
  const key = `${(fromPlan || "free").toLowerCase()}>${(toPlan || "").toLowerCase()}`;
  return UNLOCKS[key] || [];
};

/**
 * UpgradeQuoteModal — E1: shown right before Razorpay opens, when the user is
 * upgrading from one paid tier to a higher one. Shows a transparent breakdown:
 *  - Full new plan price
 *  - Credit for unused days on current plan
 *  - Final prorated amount
 *
 * For first-time subscribers (no current paid plan), this modal is skipped.
 */
export default function UpgradeQuoteModal({ open, plan, cycle, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setErr(null);
    setQuote(null);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/payments/upgrade-quote?plan=${encodeURIComponent(
      plan
    )}&cycle=${encodeURIComponent(cycle)}`;
    fetch(url, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => null);
          throw new Error(j?.detail || `Quote failed (${r.status})`);
        }
        return r.json();
      })
      .then((q) => setQuote(q))
      .catch((e) => setErr(e.message || "Could not fetch quote"))
      .finally(() => setLoading(false));
  }, [open, plan, cycle]);

  const theme = PLAN_THEME[plan] || PLAN_THEME.pro;
  const fromTheme = quote?.fromPlan ? PLAN_THEME[quote.fromPlan] : null;

  const showProration = useMemo(
    () => quote?.isUpgrade && (quote?.creditAmount || 0) > 0,
    [quote]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="upgrade-quote-modal"
    >
      <div
        className="relative w-full max-w-md bg-[#0F0F14] border border-[#2A2A35] shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
        style={{ borderColor: theme.color, borderWidth: 1 }}
      >
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: theme.color }} />

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#888] hover:text-[#F0EDE8]"
          data-testid="upgrade-quote-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 md:p-8">
          <div
            className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2"
            style={{ color: theme.color }}
          >
            — {quote?.isUpgrade ? "Upgrade preview" : "Subscription preview"}
          </div>
          <h2 className="font-display uppercase text-2xl md:text-3xl mb-1">
            {fromTheme && quote?.isUpgrade ? (
              <>
                {fromTheme.label}{" "}
                <ArrowRight className="inline w-5 h-5" style={{ color: theme.color }} />{" "}
                <span style={{ color: theme.color }}>{theme.label}</span>
              </>
            ) : (
              <>
                Activate <span style={{ color: theme.color }}>{theme.label}</span>
              </>
            )}
          </h2>
          <div className="text-xs text-[#888] uppercase tracking-[0.18em] mb-6">
            {cycle === "yearly" ? "Annual billing" : "Monthly billing"}
          </div>

          {loading && (
            <div className="text-sm text-[#888] py-6 text-center" data-testid="upgrade-quote-loading">
              Calculating prorated amount…
            </div>
          )}

          {err && !loading && (
            <div
              className="text-sm text-[#E24B4A] border border-[#E24B4A]/40 bg-[#E24B4A]/10 p-3 mb-4"
              data-testid="upgrade-quote-error"
            >
              {err}
            </div>
          )}

          {quote && !loading && (
            <>
              {/* What you'll unlock today — value-reinforcement before price */}
              {(() => {
                const unlocks = getUnlocks(quote.fromPlan, quote.toPlan);
                if (!unlocks.length) return null;
                return (
                  <div className="mb-5" data-testid="upgrade-quote-unlocks">
                    <div
                      className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2.5 flex items-center gap-2"
                      style={{ color: theme.color }}
                    >
                      <Zap className="w-3 h-3" />
                      What you'll unlock today
                    </div>
                    <ul className="space-y-1.5">
                      {unlocks.map((u, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-[12.5px] text-[#F0EDE8] leading-relaxed"
                          data-testid={`unlock-row-${i}`}
                        >
                          <span
                            className="w-1 h-1 rounded-full mt-2 shrink-0"
                            style={{ background: theme.color }}
                          />
                          <span>{u}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}

              {/* Breakdown */}
              <div className="border border-[#2A2A35] divide-y divide-[#2A2A35] mb-5">
                <Row
                  icon={IndianRupee}
                  label={`${theme.label} ${cycle} price`}
                  value={formatINR(quote.baseAmount)}
                  testid="quote-base"
                />
                {showProration && (
                  <>
                    <Row
                      icon={Calendar}
                      label={`Unused credit · ${quote.daysRemaining} ${quote.daysRemaining === 1 ? "day" : "days"} on ${(fromTheme || theme).label}`}
                      value={`− ${formatINR(quote.creditAmount)}`}
                      valueColor="#1D9E75"
                      testid="quote-credit"
                    />
                  </>
                )}
                <Row
                  icon={Sparkles}
                  label="You pay today"
                  value={formatINR(quote.proratedAmount)}
                  valueColor={theme.color}
                  bold
                  testid="quote-final"
                />
              </div>

              {showProration && (
                <p
                  className="text-xs text-[#888] leading-relaxed mb-5"
                  data-testid="upgrade-quote-explainer"
                >
                  We credit the unused {quote.daysRemaining} {quote.daysRemaining === 1 ? "day" : "days"} of your current{" "}
                  <span className="text-[#F0EDE8] font-bold">{(fromTheme || theme).label}</span> plan against your{" "}
                  <span className="text-[#F0EDE8] font-bold">{theme.label}</span> upgrade. Your renewal date stays
                  unchanged — no double charging.
                </p>
              )}

              <button
                type="button"
                onClick={() => onConfirm(quote)}
                className="w-full font-bold uppercase tracking-wider py-3 text-sm flex items-center justify-center gap-2 transition-colors"
                style={{ background: theme.color, color: "#fff" }}
                data-testid="upgrade-quote-confirm"
              >
                Continue to Razorpay <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full text-xs uppercase tracking-[0.18em] text-[#888] hover:text-[#F0EDE8] mt-3 py-2"
                data-testid="upgrade-quote-cancel"
              >
                Maybe later
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value, valueColor = "#F0EDE8", bold, testid }) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3" data-testid={testid}>
    <div className="flex items-center gap-2 min-w-0">
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: valueColor === "#F0EDE8" ? "#888" : valueColor }} />
      <span className="text-[12px] text-[#888] truncate uppercase tracking-[0.14em]">{label}</span>
    </div>
    <span
      className={`font-mono-num text-sm shrink-0 ${bold ? "text-base font-bold" : ""}`}
      style={{ color: valueColor }}
    >
      {value}
    </span>
  </div>
);
